import { useResizeObserver } from "@faire/react/hooks/useResizeObserver";
import { mergeRefs } from "@faire/slate/lib/mergeRefs";
import { useCallback, useMemo, useRef } from "react";

/**
 * Freezes a collapsible Search's expanded width as the starting point of its
 * collapse animation.
 *
 * An expanded collapsible Search is `width: 100%`, which flexbox shrinks to fit
 * alongside its siblings. On blur the collapse applies `shrink-0`, so the width
 * transition would otherwise start from the resolved `100%` — the full width of
 * the row — and visibly snap wider than the on-screen size before shrinking.
 *
 * While focused, the latest expanded width is recorded; on blur it is pinned as
 * an inline `max-width` so the collapse animates down from the real on-screen
 * width. The pin is released on focus so the Search can expand freely again.
 *
 * @param enabled Only active for the collapsible variant; a no-op otherwise.
 */
export const useCollapseWidthFreeze = (
  enabled: boolean
): {
  /** Ref for the Search container — the element whose width is frozen. */
  containerRef: React.Ref<HTMLDivElement>;
  /** Focus handler that releases the frozen width so the Search can expand. */
  onFocus: () => void;
  /** Blur handler that freezes the expanded width before the collapse starts. */
  onBlur: () => void;
} => {
  const containerRef = useRef<HTMLDivElement>(null);
  const expandedWidthRef = useRef<number | null>(null);

  // Record the expanded width only while focused. The collapse runs unfocused,
  // so its shrinking sizes are ignored and the last expanded width is kept.
  const resizeRef = useResizeObserver<HTMLDivElement>((entry) => {
    const container = entry.target;
    if (
      enabled &&
      container instanceof HTMLElement &&
      container.matches(":focus-within")
    ) {
      expandedWidthRef.current = container.getBoundingClientRect().width;
    }
  });

  const onFocus = useCallback(() => {
    if (enabled && containerRef.current) {
      containerRef.current.style.maxWidth = "";
    }
  }, [enabled]);

  const onBlur = useCallback(() => {
    const container = containerRef.current;
    const expandedWidth = expandedWidthRef.current;
    if (enabled && container && expandedWidth != null) {
      container.style.maxWidth = `${expandedWidth}px`;
    }
  }, [enabled]);

  const setContainerRef = useMemo(
    () => (enabled ? mergeRefs(containerRef, resizeRef) : containerRef),
    [enabled, resizeRef]
  );

  return { containerRef: setContainerRef, onFocus, onBlur };
};
